// Helpers for SoW product price caps on the client. The composite key MUST match the
// server's productCapKey() in eip-server/config/businessRules.ts — keep the two in sync.

import * as T from "./frontendTypes";

/** Flat key for a product cap: `${tierKey}|${category}|${task}`. */
export const productCapKey = (isTier2: boolean, category: string, task: string): string =>
  `${isTier2 ? "tier2" : "tier3"}|${category}|${task}`;

/** Inverse of productCapKey — splits a stored key back into its parts (for the admin editor). */
export const parseProductCapKey = (key: string): { tierKey: "tier2" | "tier3"; category: string; task: string } => {
  const [tierKey, category, task] = key.split("|");
  return { tierKey: tierKey === "tier3" ? "tier3" : "tier2", category, task };
};

/** Which price guardrail a work item's pre-tax total breaches. */
export type CapLevel = "none" | "soft" | "hard";

/** Result of evaluating one SoW work item against its region's effective caps. */
export interface SowItemCapEval {
  /** Highest cap the item's pre-tax total breaches (hard beats soft). */
  level: CapLevel;
  softCap: number | null;
  hardCap: number | null;
  preTax: number;
  /** Any hourly cost-breakdown line whose rate exceeds the region's hourly soft cap. */
  overHourlyRate: boolean;
  hourlyRateSoftCap: number;
}

/** Minimal shape of a SoW work item needed to evaluate its caps. */
type CapEvaluableItem = {
  category?: string;
  task?: string;
  costBreakdownTotal?: number;
  costBreakdown?: { isLumpsum?: boolean; hourlyRate?: string | number }[];
};

/**
 * Evaluate a SoW work item against a region's effective cap table. Single source of
 * truth for the reviewer soft/hard-cap highlighting (admin table, admin membership
 * viewer, IP Advisor panel) — mirrors the Service Provider submit-time gate in
 * createSOW2.tsx so reviewers and providers see the same thresholds.
 *
 * `caps` may be undefined (region caps not yet loaded / unknown region), in which
 * case the item reads as uncapped — advisory highlighting simply does not render.
 */
export const evaluateSowItemCap = (
  item: CapEvaluableItem,
  isTier2: boolean,
  caps: T.SowCapTable | undefined,
): SowItemCapEval => {
  const hourlyRateSoftCap = caps?.hourlyRateSoftCap ?? 0;
  const cap = caps?.products[productCapKey(isTier2, item.category || "", item.task || "")];
  const softCap = cap?.soft ?? null;
  const hardCap = cap?.hard ?? null;
  const preTax = item.costBreakdownTotal ?? 0;

  let level: CapLevel = "none";
  if (hardCap != null && preTax > hardCap) {
    level = "hard";
  } else if (softCap != null && preTax > softCap) {
    level = "soft";
  }

  const overHourlyRate =
    hourlyRateSoftCap > 0 &&
    (item.costBreakdown ?? []).some((cb) => {
      const rate = typeof cb.hourlyRate === "number" ? cb.hourlyRate : parseFloat(cb.hourlyRate ?? "");
      return !cb.isLumpsum && !isNaN(rate) && rate > hourlyRateSoftCap;
    });

  return { level, softCap, hardCap, preTax, overHourlyRate, hourlyRateSoftCap };
};

/** True when an item breaches any advisory cap (item total soft/hard OR an hourly-rate line). */
export const isSowItemFlagged = (evalResult: SowItemCapEval): boolean =>
  evalResult.level !== "none" || evalResult.overHourlyRate;
